/**
 * Payment status contract, kept free of imports: `paymentService` pulls in `apiClient` and the
 * router, so views and composables that only need the status values would land in that cycle.
 */

/** Values accepted by GET /api/payments `status` (see backend `PaymentListStatus`). */
export const PaymentStatus = {
    OPEN: 'open',
    PENDING: 'pending',
    CANCEL: 'cancel',
    LOW_BALANCE: 'low_balance',
    CLEARED: 'cleared',
} as const;

export type PaymentStatusValue =
    (typeof PaymentStatus)[keyof typeof PaymentStatus];

/** Everything the Other tab covers: all statuses except cleared deposits. */
export const NON_CLEARED_STATUSES: PaymentStatusValue[] = [
    PaymentStatus.OPEN,
    PaymentStatus.PENDING,
    PaymentStatus.LOW_BALANCE,
    PaymentStatus.CANCEL,
];

export const ALL_STATUSES: PaymentStatusValue[] = [
    ...NON_CLEARED_STATUSES,
    PaymentStatus.CLEARED,
];

export const CLEARED_ONLY_STATUSES: PaymentStatusValue[] = [PaymentStatus.CLEARED];

export const PENDING_ONLY_STATUSES: PaymentStatusValue[] = [PaymentStatus.PENDING];

/** Reads `?status=` (single or repeated) from the untrusted URL, keeping only allowed values. */
export function parseStatusQuery(
    value: unknown,
    allowed: PaymentStatusValue[],
): PaymentStatusValue[] {
    const raw = Array.isArray(value) ? value : [value];

    return raw.reduce<PaymentStatusValue[]>((statuses, entry) => {
        const status = entry as PaymentStatusValue;
        if (allowed.includes(status) && !statuses.includes(status)) {
            statuses.push(status);
        }
        return statuses;
    }, []);
}
